import React from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import Box from '../../components/Common/Box';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import MainLayout from '../../layouts/MainLayout';

// Chaque affirmation de cette page décrit un traitement constaté dans le code au 2026-10-01
// (modèles `users`, `payments`, `chat_messages`, `push_subscriptions`, `audit_logs` ; services
// e-mail, paiement, IA, télémétrie). Toute évolution d'un traitement doit être reportée ici.

const sectionStyle: React.CSSProperties = { marginBottom: '30px' };
const h2Style: React.CSSProperties = { fontSize: '20px', color: 'var(--text-heading)', marginBottom: '15px' };
const mutedStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: '13px' };
const listStyle: React.CSSProperties = { marginTop: '10px', paddingLeft: '25px' };
const cellStyle: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top', textAlign: 'left' };

const legalBox: React.CSSProperties = {
    background: 'rgba(99, 102, 241, 0.06)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '10px',
    lineHeight: '1.8',
    fontSize: '14px',
};

const PRESTATAIRES: { nom: string; role: string; donnees: string }[] = [
    { nom: 'Microsoft Azure', role: 'Hébergement de la plateforme et mesure d\'audience technique', donnees: 'L\'ensemble des données du compte ; pages consultées, erreurs, type d\'appareil et pays' },
    { nom: 'PayDunya', role: 'Paiement des abonnements (Mobile Money, carte)', donnees: 'Votre nom, votre adresse e-mail et le montant ; vos moyens de paiement sont saisis chez PayDunya et ne nous sont pas transmis' },
    { nom: 'Resend', role: 'Envoi des e-mails (validation du compte, alertes, lettres)', donnees: 'Votre adresse e-mail, votre nom et le contenu de l\'e-mail' },
    { nom: 'Google (Gemini)', role: 'Modèle d\'intelligence artificielle de Nia', donnees: 'Le texte de vos questions à Nia et le contexte de marché nécessaire à la réponse' },
    { nom: 'ip-api.com', role: 'Localisation approximative d\'une connexion', donnees: 'Votre adresse IP au moment de la connexion' },
    { nom: 'Google Fonts', role: 'Polices de caractères de l\'interface', donnees: 'Votre adresse IP, transmise par votre navigateur lors du chargement des polices' },
];

const PrivacyScreen: React.FC = () => {
    usePageTitle('Politique de confidentialité');
    return (
        <MainLayout>
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
                <Box>
                    <div className='box-vertical-padding box-horizontal-padding'>
                        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                            <KaneaLogo variant="horizontal" title="KaneaBourse" height={42} markColor="#6C3FC4" style={{ marginBottom: '20px' }} />
                            <h1 style={{ fontSize: '28px', color: 'var(--text-heading)', marginBottom: '10px' }}>
                                Politique de confidentialité
                            </h1>
                            <p style={mutedStyle}>Dernière mise à jour : octobre 2026</p>
                        </div>

                        <div style={{ lineHeight: '1.8', color: 'var(--text-main)' }}>
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>1. Qui traite vos données</h2>
                                <div style={legalBox}>
                                    <p style={{ margin: '0 0 6px 0' }}>
                                        <strong>Responsable du traitement :</strong> KANEA TECHNOLOGIES — RCCM n° 28463/GTCA/RC/2026 — Côte d'Ivoire
                                    </p>
                                    <p style={{ margin: 0 }}>
                                        <strong>Contact :</strong> contact@kaneabourse.com
                                    </p>
                                </div>
                                <p>
                                    Cette politique explique quelles données KaneaBourse collecte, pourquoi, à qui elles sont
                                    transmises et comment exercer vos droits. Elle complète les{' '}
                                    <Link to="/terms">Conditions générales d'utilisation</Link>.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>2. Les données que nous collectons</h2>
                                <ul style={listStyle}>
                                    <li><strong>Compte :</strong> adresse e-mail, prénom et nom, numéro de téléphone et indicatif du pays, mot de passe (conservé sous forme chiffrée, jamais en clair), code de parrainage éventuel.</li>
                                    <li><strong>Profil :</strong> pseudonyme, photo de profil, thème d'affichage, choix de participation au classement et d'affichage de votre nom sur le forum.</li>
                                    <li><strong>Utilisation du service :</strong> portefeuille virtuel et transactions simulées, favoris, alertes, notes et tâches, messages publiés sur le forum.</li>
                                    <li><strong>Conversations avec Nia :</strong> vos questions et les réponses, conservées dans votre historique.</li>
                                    <li><strong>Connexion :</strong> adresse IP, ville et pays approximatifs déduits de cette adresse, date de dernière connexion, nombre de connexions, journal des connexions et déconnexions.</li>
                                    <li><strong>Abonnement :</strong> formule choisie, montant, référence et statut du paiement, dates de début et de fin.</li>
                                    <li><strong>Notifications :</strong> l'identifiant d'abonnement fourni par votre navigateur, si vous activez les notifications.</li>
                                    <li><strong>Mesure d'audience technique :</strong> pages consultées, erreurs rencontrées, type d'appareil et de navigateur, pays.</li>
                                </ul>
                                <p style={{ marginTop: '12px' }}>
                                    Le portefeuille de KaneaBourse est une simulation : nous ne collectons aucune donnée sur vos
                                    comptes-titres ni sur vos ordres réels auprès de votre SGI, sauf si vous importez vous-même un relevé.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>3. Pourquoi nous les utilisons</h2>
                                <ul style={listStyle}>
                                    <li>Créer et sécuriser votre compte, notamment détecter une connexion depuis un autre appareil ou un partage de compte.</li>
                                    <li>Fournir les fonctions de la plateforme : portefeuille virtuel, alertes, favoris, Nia, classement.</li>
                                    <li>Gérer votre abonnement et son paiement.</li>
                                    <li>Vous envoyer les e-mails liés au service (validation du compte, alertes que vous avez créées) et, sauf opposition de votre part, nos lettres et rapports.</li>
                                    <li>Mesurer l'usage et corriger les erreurs de la plateforme.</li>
                                </ul>
                                <p style={{ marginTop: '12px' }}>
                                    Nous ne vendons pas vos données et nous ne les utilisons pas pour de la publicité ciblée.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>4. À qui elles sont transmises</h2>
                                <p>
                                    Vos données ne sont transmises qu'aux prestataires techniques nécessaires au fonctionnement
                                    du service, chacun pour la seule tâche indiquée :
                                </p>
                                <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                        <thead>
                                            <tr>
                                                <th style={cellStyle}>Prestataire</th>
                                                <th style={cellStyle}>Rôle</th>
                                                <th style={cellStyle}>Données concernées</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {PRESTATAIRES.map((p) => (
                                                <tr key={p.nom}>
                                                    <td style={{ ...cellStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.nom}</td>
                                                    <td style={cellStyle}>{p.role}</td>
                                                    <td style={cellStyle}>{p.donnees}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p style={{ marginTop: '12px' }}>
                                    Certains de ces prestataires traitent les données hors de la zone UEMOA. Si vous participez
                                    au classement, votre pseudonyme et votre performance sont visibles des autres utilisateurs ;
                                    vos messages sur le forum le sont également.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>5. Stockage sur votre appareil</h2>
                                <p>
                                    KaneaBourse n'utilise pas de cookies publicitaires. Votre navigateur conserve uniquement ce qui
                                    est nécessaire au service : votre jeton de session, votre thème d'affichage, quelques préférences
                                    d'interface et, si vous installez l'application, les fichiers permettant son fonctionnement hors ligne.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>6. Durée de conservation</h2>
                                <p>
                                    Vos données sont conservées tant que votre compte est actif. Vous pouvez demander la
                                    suppression de votre compte et de vos données à tout moment, à l'adresse indiquée ci-dessous ;
                                    les éléments que la loi nous impose de garder, comme les justificatifs de paiement, sont
                                    conservés pendant la durée légale.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>7. Vos droits</h2>
                                <ul style={listStyle}>
                                    <li><strong>Accéder</strong> à vos données et en obtenir une copie.</li>
                                    <li><strong>Rectifier</strong> vos informations : votre nom et votre pseudonyme se modifient dans votre profil.</li>
                                    <li><strong>Supprimer</strong> votre compte et vos données.</li>
                                    <li><strong>Vous opposer</strong> aux e-mails d'information : un lien de désinscription figure dans chaque lettre.</li>
                                    <li><strong>Ne pas apparaître au classement</strong> : l'option se trouve dans votre profil.</li>
                                </ul>
                                <p style={{ marginTop: '12px' }}>
                                    Pour exercer ces droits, écrivez à <strong>contact@kaneabourse.com</strong> depuis l'adresse
                                    e-mail de votre compte. Vous pouvez aussi saisir l'autorité de protection des données de votre pays.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>8. Sécurité</h2>
                                <p>
                                    Les échanges avec la plateforme sont chiffrés, les mots de passe ne sont jamais conservés en
                                    clair, et une nouvelle connexion met fin à la session ouverte sur un autre appareil. Aucun
                                    système n'étant infaillible, prévenez-nous sans attendre si vous soupçonnez un accès non
                                    autorisé à votre compte.
                                </p>
                            </section>

                            <section style={sectionStyle}>
                                <h2 style={h2Style}>9. Évolution de cette politique</h2>
                                <p>
                                    Cette politique peut être mise à jour, notamment lorsqu'un traitement ou un prestataire change.
                                    La date en tête de page indique la dernière version ; un changement important vous est signalé
                                    par e-mail ou dans l'application.
                                </p>
                            </section>
                        </div>

                        <div style={{ marginTop: '40px', textAlign: 'center', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                            <Link to="/terms" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '500' }}>
                                Conditions générales d'utilisation
                            </Link>
                        </div>
                    </div>
                </Box>
            </div>
        </MainLayout>
    );
};

export default PrivacyScreen;
